import React, { useState } from 'react';
import { CreateRapportRequest } from '../../services/reportService';
import './ReportForm.css';

interface ReportFormProps {
  onSubmit: (data: CreateRapportRequest) => void;
  onCancel: () => void;
  loading?: boolean;
}

const ReportForm: React.FC<ReportFormProps> = ({ onSubmit, onCancel, loading = false }) => {
  const [formData, setFormData] = useState({
    nomRapport: '',
    dossier: '',
    description: '',
    isPreview: true,
    file: null as File | null
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.nomRapport.trim()) {
      newErrors.nomRapport = 'Le nom du rapport est obligatoire';
    }

    if (!formData.dossier.trim()) {
      newErrors.dossier = 'Le dossier est obligatoire';
    }

    if (!formData.file) {
      newErrors.file = 'Le fichier est obligatoire';
    } else if (!formData.file.name.endsWith('.trdp')) {
      newErrors.file = 'Le fichier doit être un rapport Telerik (.trdp)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm() && formData.file) {
      onSubmit({
        nomRapport: formData.nomRapport.trim(),
        dossier: formData.dossier.trim(),
        description: formData.description.trim() || undefined,
        isPreview: formData.isPreview,
        file: formData.file
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, file }));
    
    // Effacer l'erreur du fichier si un fichier valide est sélectionné
    if (file && file.name.endsWith('.trdp')) {
      setErrors(prev => ({ ...prev, file: '' }));
    }
  };

  return (
    <div className="report-form">
      <h3>Nouveau Rapport</h3>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="nomRapport">
            Nom du rapport <span className="required">*</span>
          </label>
          <input
            type="text"
            id="nomRapport"
            value={formData.nomRapport}
            onChange={(e) => setFormData(prev => ({ ...prev, nomRapport: e.target.value }))}
            className={errors.nomRapport ? 'error' : ''}
            placeholder="Entrez le nom du rapport"
            disabled={loading}
          />
          {errors.nomRapport && <span className="error-message">{errors.nomRapport}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="dossier">
            Dossier <span className="required">*</span>
          </label>
          <input
            type="text"
            id="dossier"
            value={formData.dossier}
            onChange={(e) => setFormData(prev => ({ ...prev, dossier: e.target.value }))}
            className={errors.dossier ? 'error' : ''}
            placeholder="Entrez le nom du sous-dossier"
            disabled={loading}
          />
          {errors.dossier && <span className="error-message">{errors.dossier}</span>}
          <small className="help-text">
            Le fichier sera stocké dans : /Reports/{formData.dossier || '[dossier]'}/
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Description du rapport (optionnel)"
            rows={3}
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label>Mode d'affichage</label>
          <div className="toggle-container">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={formData.isPreview}
                onChange={(e) => setFormData(prev => ({ ...prev, isPreview: e.target.checked }))}
                disabled={loading}
              />
              <span className="toggle-text">
                {formData.isPreview ? 'Mode Aperçu avant impression (activé)' : 'Mode Interactif (désactivé)'}
              </span>
            </label>
          </div>
          <small className="help-text">
            Mode Aperçu : Affichage optimisé pour l'impression avec pagination<br/>
            Mode Interactif : Affichage continu pour la consultation à l'écran
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="file">
            Fichier .trdp <span className="required">*</span>
          </label>
          <div className="file-input-container">
            <input
              type="file"
              id="file"
              accept=".trdp"
              onChange={handleFileChange}
              className={errors.file ? 'error' : ''}
              disabled={loading}
            />
            <label htmlFor="file" className="file-input-label">
              {formData.file ? formData.file.name : 'Sélectionner un fichier .trdp'}
            </label>
          </div>
          {errors.file && <span className="error-message">{errors.file}</span>}
          <small className="help-text">
            Seuls les fichiers .trdp (Telerik Report) sont acceptés
          </small>
        </div>

        <div className="form-actions">
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Upload en cours...' : 'Créer le rapport'}
          </button>
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReportForm;
